import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { getOrganizationTranslation } from "../context/organizationOnboardingTranslations";
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  FileCheck2,
  Info,
  LayoutTemplate,
  Loader2,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const CertifCreate = () => {
    const { participantId } = useParams();
    const navigate = useNavigate();
    // ✅ Récupération des traductions
    const { t, lang } = useLanguage();
    const tr = (key, fallback) => t?.ui?.certifCreate?.[key] || t?.[key] || fallback;
    const templateTr = (key, fallback) =>
        t?.ui?.templatePolicy?.[key] ||
        getOrganizationTranslation(lang, "templatePolicy", key, fallback);
    const [formData, setFormData] = useState({
        theme: "",
        subTheme: "",
        start_date: "",
        end_date: "",
        success: false,
        templateModel: "cb4c",
    });
    const [dateMode, setDateMode] = useState("range");
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedDateInput, setSelectedDateInput] = useState("");
    const [THEMES, setTHEMES] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [userRole, setUserRole] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [loadingThemes, setLoadingThemes] = useState(true);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const [templatePolicy, setTemplatePolicy] = useState(null);
    // 🔐 Les clients et les administrateurs peuvent créer un certificat.
    useEffect(() => {
        const checkUserRole = () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/login", { replace: true });
                    return;
                }
                let role = null;
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    role = JSON.parse(storedUser)?.type || null;
                }
                // Fallback pour les anciennes sessions qui ne stockent pas `user`.
                if (!role) {
                    const encodedPayload = token.split(".")[1];
                    const normalizedPayload = encodedPayload
                        .replace(/-/g, "+")
                        .replace(/_/g, "/");
                    const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4), "=");
                    const payload = JSON.parse(atob(paddedPayload));
                    role = payload.type;
                }
                setUserRole(role);
                if (!["Client", "Admin"].includes(role)) {
                    navigate("/unauthorized", { replace: true });
                }
            }
            catch (error) {
                console.error("Error checking authentication:", error);
                navigate("/login", { replace: true });
            }
            finally {
                setCheckingAuth(false);
            }
        };
        checkUserRole();
    }, [navigate]);
    // 🧩 Récupérer les thèmes depuis le backend
    useEffect(() => {
        const fetchThemes = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_URL}/api/certifs/themes`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setTHEMES(data);
            }
            catch (err) {
                console.error("❌ Error fetching themes:", err);
            }
            finally {
                setLoadingThemes(false);
            }
        };
        fetchThemes();
    }, []);
    useEffect(() => {
        const fetchTemplatePolicy = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_URL}/api/organizations/current/template-policy`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || templateTr("templateLoadError", "Impossible de charger le modèle de certificat."));
                }
                setTemplatePolicy(data);
                if (data?.mode === "single") {
                    setFormData((current) => ({
                        ...current,
                        templateModel: "organization",
                        success: true,
                    }));
                }
            } catch (error) {
                console.error("Unable to load template policy:", error);
                setMessage(error.message || templateTr("templateLoadError", "Impossible de charger le modèle de certificat."));
            } finally {
                setLoadingTemplate(false);
            }
        };
        fetchTemplatePolicy();
    }, []);

    // 🧠 Gérer les changements de champs
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    const sortedSelectedDates = [...selectedDates].sort();
    const derivedStartDate = sortedSelectedDates[0] || "";
    const derivedEndDate = sortedSelectedDates[sortedSelectedDates.length - 1] || "";
    const displayStartDate = dateMode === "days" ? derivedStartDate : formData.start_date;
    const displayEndDate = dateMode === "days" ? derivedEndDate : formData.end_date;
    const formatDateLabel = (dateStr) => {
        if (!dateStr)
            return "";
        const parts = dateStr.split("-");
        if (parts.length !== 3)
            return dateStr;
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };
    const safeFilename = (value) => String(value || "certificat")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "_");
    const getResponseFilename = (response, fallbackName) => {
        const disposition = response.headers.get("content-disposition") || "";
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const simpleMatch = disposition.match(/filename="?([^";]+)"?/i);
        if (utf8Match?.[1]) {
            try {
                return decodeURIComponent(utf8Match[1]);
            }
            catch {
                return utf8Match[1];
            }
        }
        return simpleMatch?.[1] || fallbackName;
    };
    const downloadCertificatePdf = async (certificate) => {
        const certificateId = certificate?._id || certificate?.id;
        if (!certificateId) {
            throw new Error(tr("missingCertificateId", "Identifiant du certificat manquant."));
        }
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/certifs/download/${certificateId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || tr("downloadPdfError", "Impossible de télécharger le certificat PDF."));
        }
        const blob = await response.blob();
        const fallbackName = `${safeFilename(certificate.identifier || "certificat")}.pdf`;
        const filename = getResponseFilename(response, fallbackName);
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    };
    const addSelectedDate = () => {
        if (!selectedDateInput)
            return;
        if (selectedDates.includes(selectedDateInput)) {
            setSelectedDateInput("");
            return;
        }
        const nextDates = [...selectedDates, selectedDateInput].sort();
        setSelectedDates(nextDates);
        setSelectedDateInput("");
    };
    const removeSelectedDate = (dateToRemove) => {
        setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
    };
    // 🧩 Envoyer les données vers le backend
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");
        const payload = {
            ...formData,
            success: templatePolicy?.mode === "single" ? true : formData.success,
            date_mode: dateMode,
        };
        if (dateMode === "days") {
            const sortedDates = [...selectedDates].sort();
            if (sortedDates.length === 0) {
                setMessage(tr("selectTrainingDay", "Veuillez selectionner au moins un jour de formation."));
                setLoading(false);
                return;
            }
            payload.selected_dates = sortedDates;
            payload.start_date = sortedDates[0];
            payload.end_date = sortedDates[sortedDates.length - 1];
        }
        // ✅ Validation : Fin > Début
        if (dateMode === "range" && formData.start_date && formData.end_date) {
            const start = new Date(formData.start_date);
            const end = new Date(formData.end_date);
            if (end < start) {
                setMessage(tr("invalidEndDate", "La date de fin ne peut pas \u00EAtre ant\u00E9rieure \u00E0 la date de d\u00E9but."));
                setLoading(false);
                return;
            }
        }
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/certifs/store/${participantId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (response.ok) {
                const createdCertificate = data?.certif;
                setMessage(tr("createdDownloading", "Certificat créé avec succès. Téléchargement du PDF..."));
                try {
                    await downloadCertificatePdf(createdCertificate);
                    setMessage(tr("createdDownloaded", "Certificat créé et téléchargé automatiquement au format PDF."));
                }
                catch (downloadError) {
                    console.error("Automatic PDF download error:", downloadError);
                    setMessage(tr("downloadFailed", "Le certificat a été créé, mais son téléchargement automatique a échoué : {error}").replace("{error}", downloadError.message));
                }
                setTimeout(() => navigate(`/certificates/${participantId}`), 1200);
            }
            else {
                setMessage(data.message || tr("createError", "Erreur lors de la création."));
            }
        }
        catch (err) {
            console.error("Error:", err);
            setMessage(tr("networkError", "Erreur réseau."));
        }
        finally {
            setLoading(false);
        }
    };
    const tx = (key, fr, en) => tr(key, lang === "fr" ? fr : en);

    const fieldClass =
        "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400";

    const formatSummaryDate = (date) => {
        if (!date)
            return "—";
        return new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(new Date(`${date}T00:00:00`));
    };

    const selectedPeriod = dateMode === "range"
        ? formData.start_date && formData.end_date
            ? `${formatSummaryDate(formData.start_date)} → ${formatSummaryDate(formData.end_date)}`
            : tx("notDefined", "Non définie", "Not defined")
        : sortedSelectedDates.length
            ? tx("daysCount", "{count} jour(s)", "{count} day(s)").replace(
                "{count}",
                sortedSelectedDates.length
            )
            : tx("notDefined", "Non définie", "Not defined");

    const isSingleTemplate = templatePolicy?.mode === "single";
    const hasOrganizationTemplate = !isSingleTemplate || Boolean(templatePolicy?.template?._id);
    const selectedTemplateLabel = isSingleTemplate
        ? templatePolicy?.template?.name || templateTr("organizationModel", "Modèle officiel de l’organisation")
        : formData.templateModel === "cb4c"
            ? "CB4C"
            : "CertiLink";

    if (checkingAuth || loadingThemes || loadingTemplate) {
        return (<div className="flex min-h-[70vh] items-center justify-center bg-slate-50 px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-700 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600"/>
          {checkingAuth
                ? tx("checking", "Vérification des permissions...", "Checking permissions...")
                : loadingThemes
                    ? tx("loadingThemes", "Chargement des thèmes...", "Loading themes...")
                    : templateTr("loadingTemplate", "Chargement du modèle de certificat...")}
        </div>
      </div>);
    }

    if (!["Client", "Admin"].includes(userRole)) {
        return null;
    }

    return (<main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/certificates/${participantId}`)}
              className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4"/>
              {tx("backCertificates", "Retour aux certificats", "Back to certificates")}
            </button>

            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-600/20">
                <Award className="h-6 w-6"/>
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                  {tx("badge", "Création individuelle", "Individual creation")}
                </p>
                <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  {tr("title", lang === "fr" ? "Créer un certificat" : "Create a certificate")}
                </h1>
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 sm:self-auto">
            <CheckCircle2 className="h-4 w-4"/>
            {tx("pdfAutoDownload", "PDF téléchargé automatiquement", "PDF downloaded automatically")}
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
          <form
            onSubmit={handleSubmit}
            className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/50"
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-6 py-6 text-white sm:px-8">
              <div className="flex items-start gap-4">
                <span className="rounded-2xl bg-white/10 p-3 text-cyan-300 ring-1 ring-white/10">
                  <Sparkles className="h-6 w-6"/>
                </span>
                <div>
                  <h2 className="text-xl font-black">
                    {tx("configure", "Configurer le certificat", "Configure the certificate")}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                    {tx(
                      "configureDesc",
                      "Sélectionnez la formation, le modèle, la période et le résultat avant de générer le document.",
                      "Select the training, model, period and result before generating the document."
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8 p-6 sm:p-8">
              <section>
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-600 text-sm font-black text-white">
                    1
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">
                      {tx("training", "Formation", "Training")}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {tx("trainingDesc", "Thème, sous-thème et modèle du certificat", "Theme, sub-theme and certificate model")}
                    </p>
                  </div>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="text-sm font-bold text-slate-700">
                    {tr("mainTheme", lang === "fr" ? "Thème principal" : "Main theme")}
                    <select
                      name="theme"
                      value={formData.theme}
                      onChange={(event) => setFormData({
                        ...formData,
                        theme: event.target.value,
                        subTheme: "",
                      })}
                      className={`${fieldClass} mt-2`}
                      required
                    >
                      <option value="">
                        {tr("selectTheme", lang === "fr" ? "Sélectionner un thème" : "Select a theme")}
                      </option>
                      {Object.keys(THEMES).map((theme) => (<option key={theme} value={theme}>
                          {theme}
                        </option>))}
                    </select>
                  </label>

                  <label className="text-sm font-bold text-slate-700">
                    {tr("subTheme", lang === "fr" ? "Sous-thème" : "Sub-theme")}
                    <select
                      name="subTheme"
                      value={formData.subTheme}
                      onChange={handleChange}
                      className={`${fieldClass} mt-2`}
                      required
                      disabled={!formData.theme}
                    >
                      <option value="">
                        {tr("selectSubTheme", lang === "fr" ? "Sélectionner un sous-thème" : "Select a sub-theme")}
                      </option>
                      {formData.theme &&
                        THEMES[formData.theme] &&
                        Object.keys(THEMES[formData.theme]).map((sub) => (<option key={sub} value={sub}>
                            {sub}
                          </option>))}
                    </select>
                  </label>

                  <fieldset className="md:col-span-2">
                    <legend className="inline-flex items-center gap-2 text-sm font-bold text-slate-700">
                      <LayoutTemplate className="h-4 w-4 text-slate-400"/>
                      {t?.selectModelLabel || tx("modelChoice", "Choix du modèle", "Select a model")}
                    </legend>

                    {isSingleTemplate ? (
                      <div className={`mt-3 rounded-2xl border p-4 ${
                        hasOrganizationTemplate
                          ? "border-blue-400 bg-blue-50 ring-2 ring-blue-100"
                          : "border-rose-200 bg-rose-50"
                      }`}>
                        <span className="flex items-start gap-3">
                          <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                            hasOrganizationTemplate ? "bg-blue-600 text-white" : "bg-rose-100 text-rose-700"
                          }`}>
                            <LayoutTemplate className="h-5 w-5"/>
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-2 font-black text-slate-900">
                              {selectedTemplateLabel}
                              {hasOrganizationTemplate && <CheckCircle2 className="h-4 w-4 text-blue-600"/>}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-slate-500">
                              {hasOrganizationTemplate
                                ? templatePolicy?.template?.originalFilename ||
                                  templateTr(
                                    "organizationModelDescription",
                                    "Ce modèle PDF unique a été déposé lors de la création de l’organisation."
                                  )
                                : templateTr(
                                    "missingTemplate",
                                    "Aucun modèle PDF actif n’est configuré pour cette organisation."
                                  )}
                            </span>
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {[
                          {
                            value: "cb4c",
                            title: "CB4C",
                            description: tx("cb4cDesc", "Modèle institutionnel CB4C", "CB4C institutional model"),
                          },
                          {
                            value: "certilink",
                            title: "CertiLink",
                            description: tx("certilinkDesc", "Modèle officiel CertiLink", "Official CertiLink model"),
                          },
                        ].map((model) => {
                          const selected = formData.templateModel === model.value;
                          return (
                            <label
                              key={model.value}
                              className={`group cursor-pointer rounded-2xl border p-4 transition-all ${selected
                                ? "border-blue-400 bg-blue-50 ring-2 ring-blue-100"
                                : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"}`}
                            >
                              <input
                                type="radio"
                                name="templateModel"
                                value={model.value}
                                checked={selected}
                                onChange={(event) => setFormData({
                                  ...formData,
                                  templateModel: event.target.value,
                                })}
                                className="sr-only"
                              />
                              <span className="flex items-start gap-3">
                                <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition ${selected
                                  ? "bg-blue-600 text-white"
                                  : "bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-700"}`}>
                                  <LayoutTemplate className="h-5 w-5"/>
                                </span>
                                <span className="min-w-0">
                                  <span className="flex items-center gap-2 font-black text-slate-900">
                                    {model.title}
                                    {selected && <CheckCircle2 className="h-4 w-4 text-blue-600"/>}
                                  </span>
                                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                                    {model.description}
                                  </span>
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </fieldset>
                </div>
              </section>

              <section className="border-t border-slate-100 pt-8">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-600 text-sm font-black text-white">
                    2
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">
                      {tx("period", "Période", "Period")}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {tx("periodDesc", "Intervalle continu ou jours spécifiques", "Continuous range or specific days")}
                    </p>
                  </div>
                </div>

                <div className="mb-5 grid gap-3 sm:grid-cols-2">
                  {[
                    [
                      "range",
                      t?.dateRangeOption || tx("continuousPeriod", "Période continue", "Date range"),
                      tx("continuousPeriodDesc", "Date de début et date de fin", "Start date and end date"),
                    ],
                    [
                      "days",
                      t?.dateDaysOption || tx("specificDays", "Jours spécifiques", "Specific days"),
                      tx("specificDaysDesc", "Sélection manuelle des journées", "Manually select training days"),
                    ],
                  ].map(([value, title, subtitle]) => (<label
                    key={value}
                    className={`cursor-pointer rounded-2xl border p-4 transition ${dateMode === value
                      ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100"
                      : "border-slate-200 bg-white hover:border-blue-200"}`}
                  >
                    <input
                      type="radio"
                      name="dateMode"
                      value={value}
                      checked={dateMode === value}
                      onChange={(event) => setDateMode(event.target.value)}
                      className="sr-only"
                    />
                    <span className="flex items-start gap-3">
                      <CalendarDays className={`mt-0.5 h-5 w-5 shrink-0 ${dateMode === value ? "text-blue-600" : "text-slate-400"}`}/>
                      <span>
                        <span className="block text-sm font-black text-slate-900">{title}</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">{subtitle}</span>
                      </span>
                    </span>
                  </label>))}
                </div>

                {dateMode === "range" ? (<div className="grid gap-5 sm:grid-cols-2">
                    <label className="text-sm font-bold text-slate-700">
                      {tr("startDate", lang === "fr" ? "Date de début" : "Start date")}
                      <input
                        name="start_date"
                        type="date"
                        value={displayStartDate}
                        onChange={handleChange}
                        className={`${fieldClass} mt-2`}
                        required
                      />
                    </label>
                    <label className="text-sm font-bold text-slate-700">
                      {tr("endDate", lang === "fr" ? "Date de fin" : "End date")}
                      <input
                        name="end_date"
                        type="date"
                        value={displayEndDate}
                        onChange={handleChange}
                        className={`${fieldClass} mt-2`}
                        required
                      />
                    </label>
                  </div>) : (<div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <label className="flex-1 text-sm font-bold text-slate-700">
                        {tr("trainingDays", lang === "fr" ? "Ajouter une journée" : "Add a training day")}
                        <input
                          type="date"
                          value={selectedDateInput}
                          onChange={(event) => setSelectedDateInput(event.target.value)}
                          className={`${fieldClass} mt-2`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={addSelectedDate}
                        className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
                      >
                        <Plus className="h-4 w-4"/>
                        {t?.addDayBtn || tx("add", "Ajouter", "Add")}
                      </button>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {sortedSelectedDates.length ? (sortedSelectedDates.map((date) => (<span
                        key={date}
                        className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-bold text-cyan-800"
                      >
                        {formatDateLabel(date)}
                        <button
                          type="button"
                          aria-label={tx("removeDate", `Retirer ${date}`, `Remove ${date}`)}
                          onClick={() => removeSelectedDate(date)}
                          className="rounded-full p-0.5 hover:bg-cyan-100"
                        >
                          <X className="h-3.5 w-3.5"/>
                        </button>
                      </span>))) : (<p className="text-xs text-slate-500">
                          {tr("noDay", lang === "fr" ? "Aucun jour sélectionné." : "No day selected.")}
                        </p>)}
                    </div>

                    {sortedSelectedDates.length > 0 && (<div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-600">
                        {(t?.derivedRangeLabel || tx("derivedRange", "Période", "Period"))}: {formatDateLabel(derivedStartDate)} → {formatDateLabel(derivedEndDate)}
                        <span className="mx-2 text-slate-300">|</span>
                        {(t?.durationLabel || tx("duration", "Durée", "Duration"))}: {sortedSelectedDates.length} {t?.daysLabel || tx("days", "jours", "days")}
                      </div>)}
                  </div>)}
              </section>

              <section className="border-t border-slate-100 pt-8">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-600 text-sm font-black text-white">
                    3
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">
                      {tx("validation", "Validation", "Validation")}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {tx("validationDesc", "Indiquez le résultat associé au certificat", "Choose the result attached to the certificate")}
                    </p>
                  </div>
                </div>

                <label className={`flex cursor-pointer items-start gap-4 rounded-2xl border p-5 transition ${isSingleTemplate || formData.success
                  ? "border-emerald-300 bg-emerald-50 ring-2 ring-emerald-100"
                  : "border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40"}`}>
                  <input
                    type="checkbox"
                    name="success"
                    id="successCheck"
                    checked={isSingleTemplate || formData.success}
                    disabled={isSingleTemplate}
                    onChange={(event) => {
                      if (isSingleTemplate) return;
                      setFormData({
                        ...formData,
                        success: event.target.checked,
                      });
                    }}
                    className="sr-only"
                  />
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isSingleTemplate || formData.success
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-500"}`}>
                    <FileCheck2 className="h-5 w-5"/>
                  </span>
                  <span>
                    <span className="flex items-center gap-2 font-black text-slate-900">
                      {tr("validatedSuccess", lang === "fr" ? "Formation réussie" : "Training successfully completed")}
                      {(isSingleTemplate || formData.success) && (<CheckCircle2 className="h-4 w-4 text-emerald-600"/>)}
                    </span>
                    <span className="mt-1 block text-sm leading-6 text-slate-500">
                      {isSingleTemplate
                        ? templateTr(
                            "allCertificatesSuccessful",
                            lang === "fr"
                              ? "Pour cette organisation, chaque nouveau certificat est automatiquement enregistré comme réussite."
                              : "For this organization, every new certificate is automatically recorded as successful."
                          )
                        : tx(
                            "successHelp",
                            "Activez cette option lorsque le participant a validé la formation.",
                            "Enable this option when the participant has successfully completed the training."
                          )}
                    </span>
                  </span>
                </label>
              </section>

              {message && (<div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${message.includes(tr("successWord", "succès")) ||
                    message.includes(tr("downloadedAutomatically", "téléchargé automatiquement")) ||
                    message === t?.certCreatedSuccess
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"}`}>
                  {message.includes(tr("successWord", "succès")) ||
                    message.includes(tr("downloadedAutomatically", "téléchargé automatiquement")) ||
                    message === t?.certCreatedSuccess ? (<CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0"/>) : (<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0"/>)}
                  <p>{message}</p>
                </div>)}

              <button
                type="submit"
                disabled={loading || !hasOrganizationTemplate}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (<Loader2 className="h-5 w-5 animate-spin"/>) : (<Award className="h-5 w-5"/>)}
                {loading
                  ? tr("creating", lang === "fr" ? "Création en cours..." : "Creating...")
                  : tr("createButton", lang === "fr" ? "Créer le certificat" : "Create certificate")}
              </button>
            </div>
          </form>

          <aside className="space-y-5 xl:sticky xl:top-24 xl:self-start">
            <div className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-xl">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-cyan-300"/>
                <h2 className="font-black">
                  {tx("summary", "Résumé", "Summary")}
                </h2>
              </div>

              <div className="mt-6 space-y-4">
                {[
                  [tx("theme", "Thème", "Theme"), formData.theme || tx("notSelected", "Non sélectionné", "Not selected")],
                  [tx("subTheme", "Sous-thème", "Sub-theme"), formData.subTheme || tx("notSelected", "Non sélectionné", "Not selected")],
                  [templateTr("model", tx("model", "Modèle", "Model")), selectedTemplateLabel],
                  [tx("period", "Période", "Period"), selectedPeriod],
                  [tx("result", "Résultat", "Result"), (isSingleTemplate || formData.success) ? tx("successful", "Réussi", "Successful") : tx("participation", "Participation", "Participation")],
                ].map(([label, value]) => (<div
                  key={label}
                  className="border-b border-white/10 pb-4 last:border-0 last:pb-0"
                >
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                    {label}
                  </p>
                  <p className="mt-1 break-words text-sm font-bold text-slate-100">
                    {value}
                  </p>
                </div>))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-blue-100 bg-blue-50 p-6">
              <div className="flex items-center gap-3 text-blue-800">
                <Info className="h-5 w-5"/>
                <h2 className="font-black">
                  {tx("beforeCreation", "Avant la création", "Before creating")}
                </h2>
              </div>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-blue-900/80">
                {[
                  tx("practiceOne", "Vérifiez le thème et le sous-thème sélectionnés.", "Check the selected theme and sub-theme."),
                  tx("practiceTwo", "Confirmez les dates de la formation.", "Confirm the training dates."),
                  tx("practiceThree", "Le PDF sera téléchargé automatiquement après création.", "The PDF will be downloaded automatically after creation."),
                ].map((item) => (<li key={item} className="flex gap-2">
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-blue-600"/>
                  {item}
                </li>))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </main>);
};
export default CertifCreate;
